import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const inputVariants = cva(
  [
    "w-full min-w-0 border border-border bg-card text-foreground",
    "placeholder:text-muted-foreground",
    "transition-[color,box-shadow,border-color] outline-none",
    "file:border-0 file:bg-transparent file:text-foreground file:font-medium",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    // Focus
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    // Error
    "aria-[invalid=true]:border-(--notification-destructive-fg)",
    // Error + focus
    "aria-[invalid=true]:focus-visible:border-(--notification-destructive-fg) aria-[invalid=true]:focus-visible:ring-destructive/20 dark:aria-[invalid=true]:focus-visible:ring-destructive/40",
  ],
  {
    variants: {
      size: {
        default: "h-9 body-md px-3",
        lg: "h-10 body-md px-4",
        sm: "h-8 body-sm px-2",
        mini: "h-6 body-sm px-2",
      },
      roundness: {
        default: "rounded-sm",
        round: "rounded-full",
      },
    },
    defaultVariants: {
      size: "default",
      roundness: "default",
    },
  },
);

interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {}

function Input({
  className,
  size,
  roundness,
  "aria-invalid": ariaInvalid,
  style,
  ...props
}: InputProps) {
  const isInvalid = ariaInvalid === true || ariaInvalid === "true";
  return (
    <input
      data-slot="input"
      aria-invalid={ariaInvalid}
      style={
        isInvalid
          ? { ...style, borderColor: "var(--notification-destructive-fg)" }
          : style
      }
      className={cn(inputVariants({ size, roundness }), className)}
      {...props}
    />
  );
}

export { Input, inputVariants, type InputProps };
